import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, WrenchIcon, ZapIcon, ThermometerIcon, HomeIcon, PaintbrushIcon, SparklesIcon, TreesIcon, HammerIcon, ClockIcon, UserIcon, MapPinIcon, ShowerHeadIcon, BathIcon, ArrowUpIcon, SquareIcon, SunIcon, CookingPotIcon, LayersIcon, DoorOpenIcon, WarehouseIcon, ShieldIcon, FenceIcon, SnowflakeIcon, FootprintsIcon, BugIcon, SearchIcon, RefreshCwIcon, HelpCircleIcon, LoaderIcon, CloudRainIcon, TreePineIcon } from 'lucide-react';
import { PXFormConfig } from '../data/pxFormConfigs';
interface PXQuoteFormProps {
  config: PXFormConfig;
  initialZip: string;
  onClose: () => void;
}
// Helper to map string icon names to actual components
const getIconComponent = (iconName: string, className: string = 'w-6 h-6') => {
  const icons: Record<string, React.ReactNode> = {
    WrenchIcon: <WrenchIcon className={className} data-id="element-1122" />,
    ZapIcon: <ZapIcon className={className} data-id="element-1123" />,
    ThermometerIcon: <ThermometerIcon className={className} data-id="element-1124" />,
    HomeIcon: <HomeIcon className={className} data-id="element-1125" />,
    PaintbrushIcon: <PaintbrushIcon className={className} data-id="element-1126" />,
    SparklesIcon: <SparklesIcon className={className} data-id="element-1127" />,
    TreesIcon: <TreesIcon className={className} data-id="element-1128" />,
    HammerIcon: <HammerIcon className={className} data-id="element-1129" />,
    ClockIcon: <ClockIcon className={className} data-id="element-1130" />,
    UserIcon: <UserIcon className={className} data-id="element-1131" />,
    MapPinIcon: <MapPinIcon className={className} data-id="element-1132" />,
    ShowerHeadIcon: <ShowerHeadIcon className={className} data-id="element-1133" />,
    BathIcon: <BathIcon className={className} data-id="element-1134" />,
    ArrowUpIcon: <ArrowUpIcon className={className} data-id="element-1135" />,
    SquareIcon: <SquareIcon className={className} data-id="element-1136" />,
    SunIcon: <SunIcon className={className} data-id="element-1137" />,
    CookingPotIcon: <CookingPotIcon className={className} data-id="element-1138" />,
    LayersIcon: <LayersIcon className={className} data-id="element-1139" />,
    DoorOpenIcon: <DoorOpenIcon className={className} data-id="element-1140" />,
    WarehouseIcon: <WarehouseIcon className={className} data-id="element-1141" />,
    ShieldIcon: <ShieldIcon className={className} data-id="element-1142" />,
    FenceIcon: <FenceIcon className={className} data-id="element-1143" />,
    SnowflakeIcon: <SnowflakeIcon className={className} data-id="element-1144" />,
    FootprintsIcon: <FootprintsIcon className={className} data-id="element-1145" />,
    BugIcon: <BugIcon className={className} data-id="element-1146" />,
    SearchIcon: <SearchIcon className={className} data-id="element-1147" />,
    RefreshCwIcon: <RefreshCwIcon className={className} data-id="element-1148" />,
    HelpCircleIcon: <HelpCircleIcon className={className} data-id="element-1149" />,
    CheckCircle2Icon: <CheckCircle2Icon className={className} data-id="element-1150" />,
    CloudRainIcon: <CloudRainIcon className={className} data-id="element-1151" />,
    TreePineIcon: <TreePineIcon className={className} data-id="element-1152" />,
    WarehouseIcon: <WarehouseIcon className={className} data-id="element-1153" />,
    none: null
  };
  return icons[iconName] || <CheckCircle2Icon className={className} data-id="element-1154" />;
};
export function PXQuoteForm({
  config,
  initialZip,
  onClose
}: PXQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  // Initialize form data with standard fields
  const [formData, setFormData] = useState<Record<string, string>>({
    formName: config.formName,
    alid: '',
    oid: '',
    source: 'quote_form',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    property_ownership: '',
    taskName: config.taskName,
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Initialize dynamic fields from config
  useEffect(() => {
    const dynamicFields: Record<string, string> = {};
    config.steps.forEach(s => {
      if (!(s.field in formData)) {
        dynamicFields[s.field] = '';
      }
    });
    if (Object.keys(dynamicFields).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...dynamicFields
      }));
    }
  }, [config]);
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone formatting
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      phone_primary: formatPhoneNumber(e.target.value)
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) setFormData(prev => ({
          ...prev,
          ip: data.ip
        }));
      }).catch(() => {});
    } catch (e) {}
  }, []);
  const dynamicStepsCount = config.steps.length;
  const totalSteps = dynamicStepsCount + 3; // +3 for Homeownership, Address, Contact Info
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    const {
      cert,
      ...restFormData
    } = formData;
    const payload = {
      ...restFormData,
      TrustedFormCert: finalCert
    };
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzsuc2/?${params.toString()}`;
    console.log(`Submitting to ${config.taskName} webhook:`, webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, but continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = () => <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-1155">
      <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-1156">
        FindService<span className="text-amber-500" data-id="element-1157">Quotes</span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-1158">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-1159" />
        <span className="hidden sm:inline" data-id="element-1160">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-1161">
        <Header data-id="element-1162" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-1163">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-1164">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1165">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-1166" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1167">
              Quote Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-1168">
              Thank you, {formData.first_name}! A {config.specialistTitle}
              will contact you shortly at {formData.phone_primary} to discuss
              your project in {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors" data-id="element-1169">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-1170">
      <Header data-id="element-1171" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-1172" />

      <div className="w-full bg-slate-200 h-2" data-id="element-1173">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-1174" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-1175">
        <div className="w-full max-w-2xl" data-id="element-1176">
          <div className="mb-6 h-8" data-id="element-1177">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-1178">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1179" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-100" data-id="element-1180">
            <AnimatePresence mode="wait" custom={direction} data-id="element-1181">
              {/* DYNAMIC STEPS FROM CONFIG */}
              {step <= dynamicStepsCount && <motion.div key={`step${step}`} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1182">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-1183">
                    {config.steps[step - 1].question}
                  </h1>

                  {config.steps[step - 1].layout === 'list' && <div className="flex flex-col gap-4" data-id="element-1184">
                      {config.steps[step - 1].options.map((option, idx) => <button key={idx} onClick={() => handleOptionSelect(config.steps[step - 1].field, option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData[config.steps[step - 1].field] === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-1185">
                          {option.icon !== 'none' && <div className={`p-3 rounded-full flex-shrink-0 ${formData[config.steps[step - 1].field] === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-1186">
                              {getIconComponent(option.icon, `w-6 h-6 ${option.iconColor || ''}`)}
                            </div>}
                          <div data-id="element-1187">
                            <div className="font-bold text-slate-900 text-lg" data-id="element-1188">
                              {option.value}
                            </div>
                            {option.desc && <div className="text-slate-500 text-sm" data-id="element-1189">
                                {option.desc}
                              </div>}
                          </div>
                        </button>)}
                    </div>}

                  {config.steps[step - 1].layout === 'grid-2' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1190">
                      {config.steps[step - 1].options.map((option, idx) => <button key={idx} onClick={() => handleOptionSelect(config.steps[step - 1].field, option.value)} className={`p-6 rounded-xl border-2 text-left flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData[config.steps[step - 1].field] === option.value ? 'border-amber-500 bg-amber-50 shadow-md ring-1 ring-amber-500' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-1191">
                          {option.icon !== 'none' && <div className={`p-3 rounded-full ${formData[config.steps[step - 1].field] === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-1192">
                              {getIconComponent(option.icon, `w-6 h-6 ${option.iconColor || ''}`)}
                            </div>}
                          <span className="font-semibold text-slate-900 text-lg text-center" data-id="element-1193">
                            {option.value}
                          </span>
                        </button>)}
                    </div>}

                  {config.steps[step - 1].layout === 'grid-3' && <div className="grid grid-cols-3 gap-4" data-id="element-1194">
                      {config.steps[step - 1].options.map((option, idx) => <button key={idx} onClick={() => handleOptionSelect(config.steps[step - 1].field, option.value)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData[config.steps[step - 1].field] === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-1195">
                          <span className="font-bold text-slate-900 text-xl text-center" data-id="element-1196">
                            {option.value}
                          </span>
                        </button>)}
                    </div>}
                </motion.div>}

              {/* STANDARD STEP: Homeownership */}
              {step === dynamicStepsCount + 1 && <motion.div key="step-homeownership" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1197">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-1198">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-1199">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('property_ownership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.property_ownership === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-1200">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.property_ownership === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-1201">
                          <UserIcon className="w-6 h-6" data-id="element-1202" />
                        </div>
                        <div data-id="element-1203">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-1204">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-1205">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STANDARD STEP: Address */}
              {step === dynamicStepsCount + 2 && <motion.div key="step-address" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1206">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-1207">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-1208">
                    <div data-id="element-1209">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-1210">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-1211" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-1212">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-1213">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-1214">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1215" />
                            <span data-id="element-1216">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-1217">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-1218" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-1219" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-1220">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-1221" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-1222">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-1223">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-1224">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-1225" />
                            <span className="text-sm text-slate-500" data-id="element-1226">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1227">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STANDARD STEP: Contact Info */}
              {step === dynamicStepsCount + 3 && <motion.div key="step-contact" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-1228">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-1229">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-1230">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1231">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1232">
                      <div data-id="element-1233">
                        <label htmlFor="first_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-1234">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-1235" />
                      </div>
                      <div data-id="element-1236">
                        <label htmlFor="last_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-1237">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-1238" />
                      </div>
                    </div>

                    <div data-id="element-1239">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-1240">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-1241" />
                    </div>

                    <div data-id="element-1242">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-1243">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-1244" />
                    </div>

                    <div className="pt-4" data-id="element-1245">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-1246">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-1247" />
                        <span className="text-[11px] text-slate-500 leading-tight" data-id="element-1248">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with FindServiceQuotes, including
                          their contractors and affiliates, and up to four home
                          service companies. I authorize them to email, call, or
                          text me for marketing purposes using autodialer
                          technology, AI generated or AI assisted voice, or
                          prerecorded voice messages on the phone number I
                          provided, even if listed on a DoNotCall list. Consent
                          is not a condition to purchase. I agree to the{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-1249">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-1250">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-1251">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}